import type { ClientDoc } from '../../shared/api/converters.js';

/**
 * Pure client-list search & sort (doc 12 §4, §5, §A.3). No React, no Firestore —
 * unit-tested in isolation. Filtering/sorting runs in-memory over the live-
 * subscribed list (client bases are hundreds, not millions — doc 11 §7).
 */

export type SortMode = 'recent' | 'name' | 'bookings';
export const DEFAULT_SORT: SortMode = 'recent';
/** Sort modes in menu order. Their labels live in the catalogue under
 *  `clients.sort.*` — this module stays pure (doc 20 D8). */
export const SORT_MODES: SortMode[] = ['recent', 'name', 'bookings'];

/** Lowercase + strip diacritics, so "léa" and "Lea" match (doc 12 §5). */
export function normalizeName(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/gu, '').toLowerCase();
}

/** Keep only digits — "+33 6-12" → "33612" (doc 12 §5). */
export function digitsOf(s: string): string {
  return s.replace(/\D+/g, '');
}

/**
 * Does a client match the query? Empty query matches everything. A query with
 * any digit searches phones (with a leading-zero retry, so the national habit
 * "06 12" finds "+33 6 12") AND names; a text query matches names only
 * (doc 12 §5).
 */
export function matchesQuery(client: ClientDoc, query: string): boolean {
  const q = query.trim();
  if (!q) return true;

  const nameHit = normalizeName(client.name).includes(normalizeName(q));

  const qDigits = digitsOf(q);
  if (!qDigits) return nameHit;

  const phoneDigits = digitsOf(client.phone);
  const phoneHit =
    phoneDigits.includes(qDigits) ||
    // "06 12…" (national) should find "+33 6 12…": retry without a leading zero.
    (qDigits.startsWith('0') && phoneDigits.includes(qDigits.slice(1)));

  return phoneHit || nameHit;
}

function lastSeenMs(c: ClientDoc): number {
  return c.lastSeenAt?.getTime() ?? -1; // never-booked (no lastSeenAt) sort last
}

/** Sort a copy of the list per {@link SortMode} (doc 12 §4). Nulls-last on the
 * recency lens; name sort is locale-aware. */
export function sortClients(list: ClientDoc[], mode: SortMode, locale = 'en'): ClientDoc[] {
  const byName = (a: ClientDoc, b: ClientDoc) => a.name.localeCompare(b.name, locale);
  const rows = [...list];
  switch (mode) {
    case 'name':
      return rows.sort(byName);
    case 'bookings':
      return rows.sort(
        (a, b) => b.bookingsCount - a.bookingsCount || lastSeenMs(b) - lastSeenMs(a) || byName(a, b),
      );
    case 'recent':
    default:
      return rows.sort((a, b) => lastSeenMs(b) - lastSeenMs(a) || byName(a, b));
  }
}

/** Filter then sort — the composition the page renders (doc 12 §5). */
export function filterAndSort(
  list: ClientDoc[],
  query: string,
  mode: SortMode,
  locale = 'en',
): ClientDoc[] {
  return sortClients(list.filter((c) => matchesQuery(c, query)), mode, locale);
}

// Phone numbers are rendered app-wide as the raw E.164 string (no spaces or
// separators), matching the Calls and Dashboard views — so there is no display
// formatter here on purpose.
